import { useState, useEffect } from "react";
import { format } from "date-fns";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import {
  X, User, Robot, Fire, Thermometer, Snowflake, Target,
  CheckCircle, Globe, WhatsappLogo, ArrowSquareOut, Bell,
  CalendarCheck, Lightning, Clock, Phone, Envelope, Tag
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const cfg = {
    hot: { icon: Fire, color: "#FF3B30", bg: "rgba(255,59,48,0.12)" },
    warm: { icon: Thermometer, color: "#FFD60A", bg: "rgba(255,214,10,0.12)" },
    cold: { icon: Snowflake, color: "#64D2FF", bg: "rgba(100,210,255,0.12)" },
    converted: { icon: Target, color: "#32D74B", bg: "rgba(50,215,75,0.12)" }
  }[status] || { icon: Snowflake, color: "#64D2FF", bg: "rgba(100,210,255,0.12)" };
  const Icon = cfg.icon;

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}33` }}>
      <Icon size={14} weight="bold" /> {status}
    </span>
  );
};

const InfoRow = ({ icon: Icon, label, value, color = "#A0A0A0" }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 py-2 border-b border-[#1A1A1A] last:border-0">
      <Icon size={16} className="text-[#555] flex-shrink-0" />
      <span className="text-xs text-[#707070] w-28 flex-shrink-0">{label}</span>
      <span className="text-sm" style={{ color }}>{value}</span>
    </div>
  );
};

const SessionDetailModal = ({ sessionId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [handoffLoading, setHandoffLoading] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await axios.get(`${API}/session/${sessionId}/detail`);
        setData(res.data);
      } catch (err) {
        console.error("Failed to fetch session detail:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [sessionId]);

  const handleGenerateHandoff = async () => {
    setHandoffLoading(true);
    try {
      await axios.post(`${API}/generate-handoff-brief`, { session_id: sessionId });
      const res = await axios.get(`${API}/session/${sessionId}/detail`);
      setData(res.data);
    } catch (err) {
      console.error("Handoff generation failed:", err);
    } finally {
      setHandoffLoading(false);
    }
  };

  const handleMarkDemo = async () => {
    try {
      await axios.post(`${API}/demo-booked`, { session_id: sessionId });
      const res = await axios.get(`${API}/session/${sessionId}/detail`);
      setData(res.data);
    } catch (err) {
      console.error("Demo booking failed:", err);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center" onClick={onClose}>
        <div className="text-[#555]">Loading...</div>
      </div>
    );
  }

  if (!data) return null;

  const { session: s, messages, score_events } = data;
  const productLabels = { ultragym: "UltraGym", studio: "Studio", both: "Both" };
  const totalScore = score_events.reduce((sum, e) => sum + e.points, 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="session-detail-modal">
      <div
        className="w-full max-w-4xl max-h-[90vh] bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2A2A] bg-[#050505] flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#E85D24]/20 flex items-center justify-center">
              <User size={20} className="text-[#E85D24]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white" data-testid="modal-session-name">
                {s.visitor_name || "Anonymous"}
              </h2>
              <p className="text-xs text-[#555]">
                Session {s.id.slice(0, 8)} &middot; {format(new Date(s.created_at), "MMM d, yyyy 'at' HH:mm")}
              </p>
            </div>
            <StatusBadge status={s.lead_status} />
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors" data-testid="modal-close-btn">
            <X size={20} className="text-[#707070]" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:divide-x lg:divide-[#1A1A1A]">

            {/* Left: Session info + Score Events */}
            <div className="lg:col-span-1 p-6 space-y-5">
              {/* Score card */}
              <div className="text-center p-4 bg-[#111] border border-[#2A2A2A] rounded-lg">
                <p className="text-xs text-[#555] uppercase tracking-wider mb-1">Intent Score</p>
                <p className={`font-mono text-4xl font-black ${
                  s.score >= 65 ? 'text-[#FF3B30]' : s.score >= 30 ? 'text-[#FFD60A]' : 'text-[#64D2FF]'
                }`} data-testid="modal-score">{s.score}</p>
                <p className="text-xs text-[#555] mt-1">/100</p>
              </div>

              {/* Contact details */}
              <div>
                <h3 className="text-xs font-semibold text-[#555] uppercase tracking-wider mb-2">Contact</h3>
                <InfoRow icon={User} label="Name" value={s.visitor_name} color="#fff" />
                <InfoRow icon={Phone} label="Phone" value={s.visitor_phone} />
                <InfoRow icon={Envelope} label="Email" value={s.visitor_email} />
                <InfoRow icon={Tag} label="Product" value={productLabels[s.product_interest] || "Not identified"} color="#E85D24" />
                <InfoRow icon={Globe} label="Source" value={s.source} />
                <InfoRow icon={Lightning} label="UTM" value={s.utm_source} />
              </div>

              {/* Flags */}
              <div>
                <h3 className="text-xs font-semibold text-[#555] uppercase tracking-wider mb-2">Status Flags</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <CalendarCheck size={14} className={s.demo_booked ? "text-[#32D74B]" : "text-[#333]"} />
                    <span className={s.demo_booked ? "text-[#32D74B]" : "text-[#555]"}>Demo Booked</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <ArrowSquareOut size={14} className={s.handoff_sent ? "text-[#32D74B]" : "text-[#333]"} />
                    <span className={s.handoff_sent ? "text-[#32D74B]" : "text-[#555]"}>
                      Handoff Sent {s.handoff_initiated_at && `(${format(new Date(s.handoff_initiated_at), "MMM d, HH:mm")})`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Bell size={14} className={s.hot_alert_sent ? "text-[#FF9F0A]" : "text-[#333]"} />
                    <span className={s.hot_alert_sent ? "text-[#FF9F0A]" : "text-[#555]"}>Hot Alert Sent</span>
                  </div>
                </div>
              </div>

              {/* Score Events */}
              <div>
                <h3 className="text-xs font-semibold text-[#555] uppercase tracking-wider mb-2">Score Events ({score_events.length})</h3>
                <div className="space-y-1">
                  {score_events.map((evt) => (
                    <div key={evt.id} className="flex items-center justify-between px-2 py-1.5 bg-[#111] rounded text-xs">
                      <span className="text-[#A0A0A0]">{evt.event_type}</span>
                      <span className="font-mono font-bold text-[#E85D24]">+{evt.points}</span>
                    </div>
                  ))}
                  {score_events.length === 0 && <p className="text-xs text-[#444]">No events recorded</p>}
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-2">
                {!s.handoff_sent && (
                  <button
                    onClick={handleGenerateHandoff}
                    disabled={handoffLoading}
                    data-testid="modal-generate-handoff-btn"
                    className="w-full py-2 bg-[#BF5AF2]/20 border border-[#BF5AF2] text-[#BF5AF2] text-xs font-semibold rounded-lg hover:bg-[#BF5AF2]/30 transition-colors disabled:opacity-50"
                  >
                    {handoffLoading ? "Generating..." : "Generate Handoff Brief"}
                  </button>
                )}
                {!s.demo_booked && (
                  <button
                    onClick={handleMarkDemo}
                    data-testid="modal-mark-demo-btn"
                    className="w-full py-2 bg-[#32D74B]/20 border border-[#32D74B] text-[#32D74B] text-xs font-semibold rounded-lg hover:bg-[#32D74B]/30 transition-colors"
                  >
                    Mark Demo Booked
                  </button>
                )}
              </div>
            </div>

            {/* Right: Messages + Brief */}
            <div className="lg:col-span-2 p-6 space-y-4">
              {/* Handoff Brief */}
              {s.handoff_sent && s.handoff_brief && (
                <div className="rounded-lg border border-[#BF5AF2]/30 bg-[#BF5AF2]/5 p-4" data-testid="modal-handoff-brief">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowSquareOut size={16} weight="bold" className="text-[#BF5AF2]" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#BF5AF2]">Arya's Handoff Brief</span>
                  </div>
                  <pre className="text-xs leading-relaxed text-[#E0E0E0] whitespace-pre-wrap font-sans">{s.handoff_brief}</pre>
                </div>
              )}

              {/* Messages */}
              <div>
                <h3 className="text-xs font-semibold text-[#555] uppercase tracking-wider mb-3">
                  Conversation ({messages.length} messages)
                </h3>
                <div className="space-y-3 max-h-[450px] overflow-y-auto pr-2">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg px-4 py-2.5 ${
                        msg.role === 'user'
                          ? 'bg-[#E85D24] text-white'
                          : 'bg-[#1A1A1A] text-[#E0E0E0] border border-[#2A2A2A]'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          {msg.role === 'user' ? <User size={12} /> : <Robot size={12} className="text-[#E85D24]" />}
                          <span className="text-[10px] opacity-60">
                            {msg.role === 'user' ? s.visitor_name || 'Visitor' : 'Arya'}
                            {msg.channel && msg.channel !== 'website' && ` via ${msg.channel}`}
                            {msg.created_at && ` · ${format(new Date(msg.created_at), "HH:mm")}`}
                          </span>
                        </div>
                        <div className="text-sm leading-relaxed prose prose-invert prose-sm max-w-none prose-strong:text-[#E85D24]">
                          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{msg.content}</ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  ))}
                  {messages.length === 0 && <p className="text-xs text-[#444] text-center py-8">No messages yet</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailModal;
